import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSitemap } from 'shared/hooks/useSitemap.ts';
import { usePreview } from './usePreview';

export type Method = 'GET' | 'POST' | 'PATCH';

const apiUrl = import.meta.env.VITE_API_URL || '';
const successStatuses = [200, 201];
const notFoundRedirectStatuses = [403, 404, 500];
const serviceUnavailableStatus = 503;

interface UseApiOptions<TData, TDto> {
  mapFn?: (dto: TDto) => TData;
  lazy?: boolean;
  injectDashboardId?: boolean;
}

export const useApi = <TData, TDto = any>(
  endpoint?: string,
  options?: UseApiOptions<TData, TDto>,
) => {
  const { dashboardId } = useParams();
  const { previewToken } = usePreview();
  const sitemap = useSitemap();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { mapFn, injectDashboardId = true, lazy = false } = options ?? {};

  const requestApi = useCallback(
    async (reqEndpoint: string, body?: any, method: Method = 'GET') => {
      const baseUrl = `${apiUrl}${injectDashboardId ? `${dashboardId}/` : ''}${reqEndpoint}`;
      const urlWithParams = previewToken ? `${baseUrl}?previewToken=${previewToken}` : baseUrl;

      try {
        setLoading(true);
        const res = await fetch(urlWithParams, {
          method,
          headers: {
            'Content-Type': 'application/json',
          },
          body: body ? JSON.stringify(body) : undefined,
          credentials: 'include',
        });
        if (!successStatuses.includes(res.status) || !res) {
          const err: any = new Error(`Error during API request: ${res.statusText}`);
          err.statusCode = res.status;
          throw err;
        }
        return res.json().catch(() => null);
      } catch (err: any) {
        if (notFoundRedirectStatuses.includes(err.statusCode || 500)) {
          navigate(sitemap.notFound);
        }
        if (err.statusCode === serviceUnavailableStatus) {
          navigate(sitemap.serviceUnavailable);
        }
        console.error(err);
        setError((err as Error).message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [endpoint, injectDashboardId],
  );

  const fetchData = useCallback(async () => {
    if (!endpoint) {
      return;
    }
    const apiData = await requestApi(endpoint);
    if (!apiData) {
      setData(null);
      return;
    }
    setData(mapFn ? mapFn(apiData) : apiData);
  }, [requestApi, mapFn, endpoint]);

  useEffect(() => {
    if (!lazy) {
      fetchData();
    }
  }, [endpoint, lazy]);

  return { data, error, loading, refetch: fetchData, requestApi };
};
